// ./src/components/features/Timeline/VenueSelect/VenueSelect.tsx
//
// A self-contained, searchable picker for a saved venue of a given kind
// (VENUE_KIND.VENUE = a place, or VENDOR = a service provider). Fetches the
// account's venues of that kind once on open via VenueService; no provider
// needed. Emits (id, label) — the label is an optimistic snapshot (the server
// re-derives it on save). A `fallbackLabel` keeps a preselected value readable
// even if it falls outside the fetched page.
import React, { useState, useEffect, useMemo, useCallback } from "react";

import { Venue, VenueService, listAllPages } from "@/utils/api";
import { DRAWER_COMBOBOX_PROPS } from "@/utils";
import { AppIcon, SelectField } from "@/components/generic";

export interface VenueSelectProps {
  /** VENUE_KIND.VENUE | VENUE_KIND.VENDOR — which lens of the venue table to list. */
  kind: string;
  value: string | null;
  onChange: (id: string | null, label: string | null) => void;
  label?: string;
  description?: string;
  placeholder?: string;
  fallbackLabel?: string | null;
  error?: React.ReactNode;
  disabled?: boolean;
  size?: string;
}

export const VenueSelect: React.FC<VenueSelectProps> = ({
  kind,
  value,
  onChange,
  label,
  description,
  placeholder = "Search…",
  fallbackLabel,
  error,
  disabled,
  size = "md",
}) => {
  const [venues, setVenues] = useState<Venue[]>([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    let cancelled = false;

    const load = async () => {
      setLoading(true);
      const result = await listAllPages(VenueService.list, { kind: [kind] });

      if (cancelled) {
        return;
      }

      if (result.didSucceed()) {
        setVenues((result.data as Venue[]) || []);
      }

      setLoading(false);
    };

    load();

    return () => {
      cancelled = true;
    };
  }, [kind]);

  // Options from the fetched list, with the preselected value appended if it is
  // not present (so an edit-mode value stays readable).
  const data = useMemo(() => {
    const options = venues
      .filter((v) => v.id)
      .map((v) => ({ value: v.id as string, label: v.name || "Untitled" }));

    if (value && !options.some((o) => o.value === value)) {
      options.unshift({ value, label: fallbackLabel || "Selected" });
    }

    return options;
  }, [venues, value, fallbackLabel]);

  const handleChange = useCallback(
    (id: string | null) => {
      const match = data.find((o) => o.value === id);
      onChange(id, match?.label ?? null);
    },
    [data, onChange]
  );

  return (
    <SelectField
      label={label}
      description={description}
      placeholder={loading ? "Loading…" : placeholder}
      data={data}
      value={value}
      onChange={(_name, v) => handleChange(v)}
      leftSection={<AppIcon iconName={kind === "vendor" ? "Briefcase" : "MapPin"} size={16} />}
      error={error}
      disabled={disabled}
      searchable
      clearable
      nothingFoundMessage={loading ? "Loading…" : "No matches"}
      comboboxProps={DRAWER_COMBOBOX_PROPS}
      size={size}
    />
  );
};

export default VenueSelect;
